import { Injectable, inject } from '@angular/core';
import { Firestore, collection, collectionData, doc, setDoc, updateDoc, writeBatch } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { BrandService } from './brand.service';
import { Brand } from '../models/brand.model';
import { brandIssuerName, brandScopeLabel, normalizeBrandScope, validateIssuanceBrandScope } from './coupon-brand.util';

export interface Coupon {
  id?: string;                // Same as code (Firestore Document ID)
  code: string;               // 12-character alphanumeric random code
  title: string;              // Name/Description (e.g. 'Opening Promo $5 OFF')
  discountType: 'fixed' | 'percent' | 'free_session'; // 'fixed' ($ off), 'percent' (% off), 'free_session' (100% off)
  discountValue: number;      // e.g. 5.00 ($5), 20 (20%), 0 (free_session)
  
  // Scope / Targeting
  applicableStores?: string[]; // Array of store IDs or ['all']
  applicableBrands?: string[]; // Canonical brands/{id} document ids, or exactly ['all'] when deliberately chosen. Required to issue (KAN-227)
  
  // Issuance & Distribution
  recipientName?: string;     // Recipient Name
  recipientEmail?: string;    // Recipient Email
  issuedBy?: string;          // Email/UID of issuing user
  issuedAt: any;              // Timestamp / Date
  expiresAt?: any;            // Expiration date (ISO string or Timestamp or null)

  // Usage & Limits
  usageType: 'single_use' | 'multi_use'; // Default: 'single_use'
  maxRedemptions: number;     // Default: 1 for single_use, configurable for multi_use
  timesRedeemed: number;      // Number of times redeemed (starts at 0)
  
  // Status & Lifecycle
  status: 'active' | 'redeemed' | 'expired' | 'revoked';
  isActive: boolean;          // Active toggle (default true)
  isDeleted?: boolean;        // Soft delete flag
  
  // Redemption Audit
  redeemedAt?: any;           // Last redeemed timestamp
  redeemedDeviceId?: string;  // Device ID where coupon was redeemed
  redeemedStoreId?: string;   // Store ID where coupon was redeemed
  redeemedSessionId?: string; // Kiosk session ID
}

@Injectable({
  providedIn: 'root'
})
export class CouponService {
  private firestore = inject(Firestore);
  private auth = inject(Auth);
  private configService = inject(ConfigService);
  private brandService = inject(BrandService);
  private collectionName = 'coupons';

  /**
   * Validates and normalizes the brand scope against the canonical brands collection.
   * A coupon is never issued without an explicit scope: a missing or empty scope throws instead of defaulting to ['all'].
   * KAN-363: exactly one brand (never LFC) or exactly ['all']; multi-brand scopes are rejected without writing.
   */
  private async resolveBrandScope(applicableBrands: string[] | undefined | null): Promise<string[]> {
    const brands = await firstValueFrom(this.brandService.getBrands());
    const error = validateIssuanceBrandScope(applicableBrands, brands);
    if (error) throw new Error(error);
    return normalizeBrandScope(applicableBrands);
  }

  /**
   * Generates a 12-character random uppercase alphanumeric string.
   * Excludes ambiguous characters (0, O, 1, I) for human clarity.
   */
  generateCouponCode(): string {
    const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
    let result = '';
    for (let i = 0; i < 12; i++) {
      const randomIndex = Math.floor(Math.random() * chars.length);
      result += chars[randomIndex];
    }
    return result;
  }

  private escapeHtml(value: string): string {
    return String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /**
   * Format code into groups of 4 for better readability (e.g. ABCD-EFGH-JKLM)
   */
  formatDisplayCode(code: string): string {
    if (!code || code.length !== 12) return code;
    return `${code.slice(0, 4)}-${code.slice(4, 8)}-${code.slice(8, 12)}`;
  }

  /**
   * Get all active coupons (not soft-deleted)
   */
  getCoupons(): Observable<Coupon[]> {
    const col = collection(this.firestore, this.collectionName);
    return (collectionData(col, { idField: 'id' }) as Observable<Coupon[]>).pipe(
      map(coupons => coupons.filter(c => !c.isDeleted))
    );
  }

  /**
   * Add a single coupon with code as Document ID
   */
  async addCoupon(couponData: Partial<Coupon>): Promise<string> {
    const code = (couponData.code && couponData.code.trim().length === 12)
      ? couponData.code.trim().toUpperCase()
      : this.generateCouponCode();

    const brandScope = await this.resolveBrandScope(couponData.applicableBrands);
    const currentUserEmail = this.auth.currentUser?.email || 'admin';
    const isSingleUse = (couponData.usageType || 'single_use') === 'single_use';

    const newCoupon: Coupon = {
      id: code,
      code: code,
      title: couponData.title || 'Special Promotion',
      discountType: couponData.discountType || 'fixed',
      discountValue: couponData.discountType === 'free_session' ? 0 : (couponData.discountValue || 0),
      applicableStores: couponData.applicableStores || ['all'],
      applicableBrands: brandScope,
      recipientName: couponData.recipientName || '',
      recipientEmail: couponData.recipientEmail || '',
      issuedBy: currentUserEmail,
      issuedAt: new Date().toISOString(),
      expiresAt: couponData.expiresAt || null,
      usageType: isSingleUse ? 'single_use' : 'multi_use',
      maxRedemptions: isSingleUse ? 1 : (couponData.maxRedemptions || 1),
      timesRedeemed: 0,
      status: 'active',
      isActive: couponData.isActive !== false,
      isDeleted: false
    };

    const docRef = doc(this.firestore, this.collectionName, code);
    await setDoc(docRef, newCoupon);
    return code;
  }

  /**
   * Add bulk coupons with common attributes and unique 12-char codes
   */
  async addBulkCoupons(template: Partial<Coupon>, count: number): Promise<string[]> {
    if (count <= 0) return [];
    const brandScope = await this.resolveBrandScope(template.applicableBrands);
    const maxPerBatch = 450; // Firestore batch limit is 500
    const generatedCodes: string[] = [];
    const currentUserEmail = this.auth.currentUser?.email || 'admin';
    const isSingleUse = (template.usageType || 'single_use') === 'single_use';

    let currentBatch = writeBatch(this.firestore);
    let batchCount = 0;

    for (let i = 0; i < count; i++) {
      const code = this.generateCouponCode();
      generatedCodes.push(code);

      const couponDoc: Coupon = {
        id: code,
        code: code,
        title: template.title || 'Promotional Coupon',
        discountType: template.discountType || 'fixed',
        discountValue: template.discountType === 'free_session' ? 0 : (template.discountValue || 0),
        applicableStores: template.applicableStores || ['all'],
        applicableBrands: brandScope,
        recipientName: '',
        recipientEmail: '',
        issuedBy: currentUserEmail,
        issuedAt: new Date().toISOString(),
        expiresAt: template.expiresAt || null,
        usageType: isSingleUse ? 'single_use' : 'multi_use',
        maxRedemptions: isSingleUse ? 1 : (template.maxRedemptions || 1),
        timesRedeemed: 0,
        status: 'active',
        isActive: true,
        isDeleted: false
      };

      const docRef = doc(this.firestore, this.collectionName, code);
      currentBatch.set(docRef, couponDoc);
      batchCount++;

      if (batchCount >= maxPerBatch) {
        await currentBatch.commit();
        currentBatch = writeBatch(this.firestore);
        batchCount = 0;
      }
    }

    if (batchCount > 0) {
      await currentBatch.commit();
    }

    return generatedCodes;
  }

  /**
   * Update coupon
   */
  async updateCoupon(code: string, data: Partial<Coupon>): Promise<void> {
    const docRef = doc(this.firestore, this.collectionName, code);
    if (data.applicableBrands !== undefined) {
      // Changing the scope needs an explicit, valid selection (no implicit ['all']).
      data = { ...data, applicableBrands: await this.resolveBrandScope(data.applicableBrands) };
    }
    return updateDoc(docRef, data);
  }

  /**
   * Soft delete coupon
   */
  async deleteCoupon(code: string): Promise<void> {
    const docRef = doc(this.firestore, this.collectionName, code);
    return updateDoc(docRef, { isDeleted: true, status: 'revoked' });
  }

  /**
   * Send coupon email to recipient with optional custom subject, title, and body
   */
  async sendCouponEmail(
    coupon: Coupon, 
    customData?: { subject?: string; customTitle?: string; customBody?: string }
  ): Promise<void> {
    if (!coupon.recipientEmail) {
      throw new Error('Recipient email is required.');
    }

    const fromEmail = this.configService.getConfig().adminEmail || 'noreply@photolavie.com';
    const currentUserEmail = this.auth.currentUser?.email || 'admin';
    const formattedCode = this.formatDisplayCode(coupon.code);

    let discountDisplay = '';
    if (coupon.discountType === 'free_session') {
      discountDisplay = '100% FREE SESSION';
    } else if (coupon.discountType === 'percent') {
      discountDisplay = `${coupon.discountValue}% OFF`;
    } else {
      discountDisplay = `$${Number(coupon.discountValue || 0).toFixed(2)} OFF`;
    }

    const expiryDisplay = coupon.expiresAt 
      ? new Date(coupon.expiresAt).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'short', day: 'numeric' }) + ' (Pacific Time)'
      : 'No Expiration';

    // Brand names come from the canonical brands collection (never hardcoded): the brand(s) the coupon is valid for, or "All brands".
    const brands: Brand[] = await firstValueFrom(this.brandService.getBrands());
    const brandLabel = this.escapeHtml(brandScopeLabel(coupon.applicableBrands, brands));
    const issuerName = this.escapeHtml(brandIssuerName(coupon.applicableBrands, brands));

    const recipientGreeting = coupon.recipientName ? `Hello ${coupon.recipientName},` : 'Hello,';
    const emailTitle = customData?.customTitle?.trim() || coupon.title;
    const bodyNote = customData?.customBody?.trim() || `You have received an exclusive photo booth voucher valid for ${brandLabel}! Scan your QR code at the kiosk payment screen or enter your 12-digit code.`;
    const emailSubject = customData?.subject?.trim() || `You have received a new Coupon from ${issuerName}!`;

    // Email clients (Gmail, Outlook, Yahoo, etc.) block base64 data: URLs for security.
    // Using a direct HTTPS QR generator URL ensures the image displays reliably in all email clients.
    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(coupon.code)}`;
    const formattedBodyHtml = bodyNote.replace(/\n/g, '<br/>');

    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 24px; background-color: #f8fafc; border-radius: 12px; border: 1px solid #e2e8f0;">
        <div style="text-align: center; margin-bottom: 24px;">
          <h1 style="color: #1e293b; margin: 0 0 8px 0; font-size: 24px; font-weight: 700;">${issuerName} Coupon</h1>
          <p style="color: #64748b; margin: 0; font-size: 14px;">Your exclusive photo booth voucher</p>
        </div>
        
        <div style="background: white; border-radius: 10px; padding: 24px; text-align: center; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); border: 1px solid #e2e8f0;">
          <p style="font-size: 16px; color: #334155; margin-top: 0;">${recipientGreeting}</p>
          <div style="font-size: 14px; color: #475569; margin: 12px 0; line-height: 1.6;">${formattedBodyHtml}</div>
          
          <div style="margin: 20px 0; padding: 16px; background-color: #f0fdf4; border: 1px dashed #22c55e; border-radius: 8px;">
            <div style="font-size: 28px; font-weight: 800; color: #15803d; letter-spacing: 0.5px;">${discountDisplay}</div>
            <div style="font-size: 14px; font-weight: 600; color: #166534; margin-top: 4px;">${emailTitle}</div>
          </div>

          <div style="margin: 24px 0;">
            <img src="${qrImageUrl}" alt="Coupon QR Code" width="200" height="200" style="width: 200px; height: 200px; display: inline-block; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px; background: white;" />
            <div style="margin-top: 12px; font-family: monospace; font-size: 18px; font-weight: 700; color: #0f172a; letter-spacing: 2px;">
              ${formattedCode}
            </div>
          </div>

          <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid #f1f5f9; font-size: 13px; color: #475569; line-height: 1.6;">
            <div><strong>Expiration Date:</strong> <span style="color: #dc2626; font-weight: 600;">${expiryDisplay}</span></div>
            <div><strong>Valid for:</strong> ${brandLabel}</div>
            <div><strong>Usage:</strong> ${coupon.usageType === 'single_use' ? 'Single Use Only' : 'Multi-use Voucher'}</div>
          </div>
        </div>

        <div style="text-align: center; margin-top: 24px; color: #94a3b8; font-size: 12px;">
          <p style="margin: 0 0 4px 0;">How to use: Tap "Coupon" on the payment screen and hold this QR code up to the camera, or you can also manually enter the code shown above</p>
          <p style="margin: 0;">© ${issuerName}. All rights reserved.</p>
        </div>
      </div>
    `;

    const textContent = `${issuerName} Coupon\n\n${recipientGreeting}\n\n${bodyNote}\n\nDiscount: ${discountDisplay}\nTitle: ${emailTitle}\nValid for: ${brandLabel}\nCoupon Code: ${formattedCode}\nExpiration Date: ${expiryDisplay}\n\nScan the QR code or enter your code on the kiosk payment screen.`;

    const emailData = {
      senderType: 'coupon',
      fromName: issuerName,
      to: [coupon.recipientEmail],
      from: fromEmail,
      replyTo: fromEmail,
      subject: emailSubject,
      message: {
        subject: emailSubject,
        html: htmlContent,
        text: textContent
      },
      html: htmlContent,
      text: textContent,
      sentAt: new Date(),
      sentBy: currentUserEmail
    };

    const emailReceiptsCol = collection(this.firestore, 'email_receipts');
    const newEmailRef = doc(emailReceiptsCol);
    await setDoc(newEmailRef, emailData);
  }
}
